import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { validatePassword, validateConfirmPassword, validateForm } from '../../../utils/validation';
import { toast } from 'react-toastify';
import { changePassword } from '../../../services/userService';

function ChangePassword() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordVisible, setPasswordVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letters: false,
    numbers: false,
    special: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validationRules = {
    currentPassword: (value) => value ? [] : ['Current password is required'],
    newPassword: validatePassword,
    confirmPassword: (value) => validateConfirmPassword(value, formData.newPassword)
  };

  const checkPasswordRequirements = (password) => ({
    length: password.length >= 8,
    letters: /[a-zA-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    const newFormData = { ...formData, [id]: value };
    const newErrors = { ...errors, [id]: undefined };

    if (id === 'newPassword') {
      setPasswordRequirements(checkPasswordRequirements(value));
      if (newFormData.confirmPassword) {
        const confirmErrors = validateConfirmPassword(newFormData.confirmPassword, value);
        if (confirmErrors.length > 0) {
          newErrors.confirmPassword = confirmErrors;
        }
      }
    }

    if (id === 'confirmPassword') {
      const confirmErrors = validateConfirmPassword(value, newFormData.newPassword);
      if (confirmErrors.length > 0) {
        newErrors.confirmPassword = confirmErrors;
      }
    }

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisible(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm(formData, validationRules);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        toast.success(response.message || response?.data?.message || 'Password updated successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setErrors({});
      } else {
        toast.error(
          response?.data?.error_msg ||
          response?.message ||
          'Current password is invalid.'
        );
      }
    } catch (error) {
      console.error("❌ Error changing password:", error);
      toast.error(
        error.response?.data?.data?.error_msg ||
        error.response?.data?.error_msg ||
        error.response?.data?.message ||
        "Something went wrong"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderInputField = (label, id, value, visible, onChange, error, placeholder) => (
    <div className="md:col-span-12 mb-3">
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="relative">
          <input
            type={visible ? "text" : "password"}
            className={`w-full px-3 py-2 border ${error ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 pr-10 ${error ? 'is-invalid' : ''}`}
            id={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none z-10"
            onClick={() => togglePasswordVisibility(id)}
            disabled={isLoading}
          >
            <Icon icon={visible ? "mdi:eye-off" : "mdi:eye"} width="18" height="18" />
          </button>
          {error && <div className="block text-red-500 text-sm mt-1">{error[0]}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-200">
          <h5 className="text-lg font-semibold">Change Password</h5>
        </div>
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-1/2">

                {renderInputField(
                  "Current Password",
                  "currentPassword",
                  formData.currentPassword,
                  passwordVisible.currentPassword,
                  handleChange,
                  errors.currentPassword,
                  "Enter current password"
                )}

                {renderInputField(
                  "New Password",
                  "newPassword",
                  formData.newPassword,
                  passwordVisible.newPassword,
                  handleChange,
                  errors.newPassword,
                  "Enter new password"
                )}

                {/* Password Requirements */}
                <div className="flex flex-wrap gap-2 mt-2">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${passwordRequirements.length ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    <Icon icon={passwordRequirements.length ? "mdi:check" : "mdi:close"} className="w-4 h-4" /> 8+ Characters
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${passwordRequirements.letters ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    <Icon icon={passwordRequirements.letters ? "mdi:check" : "mdi:close"} className="w-4 h-4" /> Letters
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${passwordRequirements.numbers ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    <Icon icon={passwordRequirements.numbers ? "mdi:check" : "mdi:close"} className="w-4 h-4" /> Numbers
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${passwordRequirements.special ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    <Icon icon={passwordRequirements.special ? "mdi:check" : "mdi:close"} className="w-4 h-4" /> Special Chars
                  </div>
                </div>

                {renderInputField(
                  "Confirm New Password",
                  "confirmPassword",
                  formData.confirmPassword,
                  passwordVisible.confirmPassword,
                  handleChange,
                  errors.confirmPassword,
                  "Confirm new password"
                )}

                {/* Submit */}
                <div className="w-full mt-3 flex justify-center">
                  <button type="submit" className={`bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-1/2 ${isLoading ? 'loading opacity-75 cursor-not-allowed' : ''}`} disabled={isLoading}>
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" role="status" />
                        Changing Password...
                      </span>
                    ) : 'Change Password'}
                  </button>
                </div>

              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
